import { Form } from './form.js';
import {
    disabledProps,
    DisabledPropsInterface,
    useDisableComposable,
} from '../../application/composables/disabled-composable.js';
import { computed, getCurrentInstance, nextTick, onBeforeMount, onBeforeUnmount, ref, unref, watch } from 'vue';

export interface FormPartProps extends DisabledPropsInterface {
    form?: Form,
    name?: string,
    required?: boolean,
    emitInvalid?: boolean,
    onSubmit?: (value: any) => void,
    immediateValidation?: boolean,
}

/**
 * @deprecated
 */
export const formPartProps = {
    form: {
        type: Form,
        default: null,
    },
    name: {
        type: String,
        default: null,
    },
    required: {
        type: Boolean,
        default: false,
    },
    emitInvalid: {
        type: Boolean,
        default: false,
    },
    onSubmit: {
        type: Function,
        default() {
            return () => null;
        },
    },
    immediateValidation: {
        type: Boolean,
        default: false,
    },
    ...disabledProps,
};

export type FormPartEmitsType = {
    (event: 'invalid'): void,
    (event: 'valid'): void,
};

/**
 * @deprecated
 */
export const formPartEmits = ['invalid', 'valid'];

export function useFormPartComposable(value: any, validation: () => true|string = () => true) {
    const instance = getCurrentInstance();
    if (instance === null) {
        throw new Error('impossible to handle formPart - instance is null');
    }
    const { props, emit }: { props: FormPartProps, emit: FormPartEmitsType } = instance;

    const validationMessage = ref<string|null>(null);

    const required = computed(() => props.required === true);

    onBeforeMount(() => {
        if (!props.form || !props.name) {
            return;
        }
        const { disabled } = useDisableComposable();
        props.form.addField(props.name, instance, required, validation, value, props.emitInvalid === true, disabled.value);
    });

    onBeforeUnmount(() => {
        if (!props.form || !props.name) {
            return;
        }
        props.form.removeField(props.name);
    });

    function validate() {
        validationMessage.value = null;
        const isValid = validation();
        if (isValid !== true) {
            validationMessage.value = isValid;
            emit('invalid');
            return;
        }

        if (props.required && (unref(value) === null || unref(value) === undefined)) {
            validationMessage.value = 'Das muss ausgefüllt werden';
            emit('invalid');
            return;
        }

        emit('valid');
    }

    if (props.immediateValidation) {
        watch(value, () => {
            validate();
        });
    }

    async function submit() {
        await nextTick();
        validate();

        if (validationMessage.value !== null) {
            if (instance !== null && instance.exposed !== null && instance.exposed.focus !== undefined) {
                instance.exposed.focus();
            }
            if (!props.emitInvalid) {
                return false;
            }
        }

        if (props.onSubmit) {
            await props.onSubmit(unref(value));
        }
        if (props.form && props.name) {
            return Promise.resolve(props.form.submit([props.name]));
        }
        return true;
    }

    return {
        submit,
        validationMessage,
        validate,
        exposes: {
            submit,
        },
    };
}
